import { supabase } from "@/integrations/supabase/client";

export interface CreateNotificationInput {
  entity_type: string;
  entity_id: string;
  entity_label?: string | null;
  project_id?: string | null;
  type: string;
  title: string;
  message?: string | null;
  priority?: "urgent" | "high" | "normal" | "low";
  action_label?: string | null;
  action_url?: string | null;
  assigned_to?: string | null;
  due_date?: string | null;
  created_by?: string | null;
}

/**
 * Create an in-app notification. Non-blocking — errors are logged but not thrown.
 * The Supabase realtime subscription in useNotifications will pick it up instantly.
 */
export async function createNotification(input: CreateNotificationInput): Promise<void> {
  try {
    const { data: companyId } = await supabase.rpc("my_company_id");
    if (!companyId) return;

    // Auto-populate created_by from current session if not provided
    let createdBy = input.created_by ?? null;
    if (!createdBy) {
      const { data: { user } } = await supabase.auth.getUser();
      createdBy = user?.id ?? null;
    }

    const { error } = await supabase.from("notifications").insert({
      company_id: companyId,
      entity_type: input.entity_type,
      entity_id: input.entity_id,
      entity_label: input.entity_label ?? null,
      project_id: input.project_id ?? null,
      type: input.type,
      title: input.title,
      message: input.message ?? null,
      priority: input.priority ?? "normal",
      status: "unread",
      action_label: input.action_label ?? null,
      action_url: input.action_url ?? null,
      assigned_to: input.assigned_to ?? null,
      due_date: input.due_date ?? null,
      created_by: createdBy,
    } as any);

    if (error) {
      console.error("[createNotification] Insert error:", error.message, error.details);
    }
  } catch (e) {
    console.error("[createNotification] Failed:", e);
  }
}

/**
 * Batch-create notifications for multiple recipients.
 */
export async function createNotifications(inputs: CreateNotificationInput[]): Promise<void> {
  if (inputs.length === 0) return;
  try {
    const { data: companyId } = await supabase.rpc("my_company_id");
    if (!companyId) return;

    await supabase.from("notifications").insert(
      inputs.map((input) => ({
        company_id: companyId,
        entity_type: input.entity_type,
        entity_id: input.entity_id,
        entity_label: input.entity_label ?? null,
        project_id: input.project_id ?? null,
        type: input.type,
        title: input.title,
        message: input.message ?? null,
        priority: input.priority ?? "normal",
        status: "unread",
        action_label: input.action_label ?? null,
        action_url: input.action_url ?? null,
        assigned_to: input.assigned_to ?? null,
        due_date: input.due_date ?? null,
        created_by: input.created_by ?? null,
      })) as any
    );
  } catch (e) {
    console.error("[createNotifications] Failed:", e);
  }
}
